import React, { useContext, useState } from "react";
import { sum } from "lodash";
import { Material } from "../../../../data/entities";
import CustomTable from "../../../../shared/components/CustomTable";
import { StyledButton } from "../../Styled";
import { TableRow } from "@mui/material";
import { StyledTableCell } from "../../../../shared/components/CustomTable/Styled";
import TextBox from "../../../../shared/components/TextBox";
import IconButton from "../../../../shared/components/IconButton";
import DeleteIcon from "../../../../shared/svg/DeleteIcon";
import {
    SummaryTableCell,
    SummaryTableRow,
} from "../../../Provider/create/Content/MaterialList/AddMaterialModal/Styled";
import { ContentWrapper, ProductButtonRow, ProductTableText } from "../../../ProductOrder/single/Styled";
import { AddMaterialModal } from "../AddMaterialModal";
import {
    formatCurrency,
    formatNumber,
    formatInternationalNumber,
    parseInternationalToNumber,
} from "../../../../utils/format";
import { color } from "../../../../shared/styles";
import { TableWarningMessage } from "../../../ProductOrder/single/AddProductModal/Styled";
import WarningTableIcon from "../../../../shared/svg/WarningTableIcon";
import { StyledNavLink } from "../../../../shared/styled/TableStyles";
import { AuthenticationContext } from "../../../../provider/AuthenticationProvider";
import { ROLE } from "../../../../data/enums";
import { error } from "../../../../utils/toast";

export const MaterialTableColumns = [
    { text: "Mã vật tư", icon: <></> },
    { text: "Tên vật tư", icon: <></> },
    { text: "Đơn vị", icon: <></> },
    { text: "Giá cung cấp", icon: <></> },
    { text: "Số lượng yêu cầu", icon: <></> },
    { text: "", icon: <></> },
];

interface ProductRowProps {
    item: any;
    extraRowProps: any;
}

export const MaterialRow = ({ item, extraRowProps }: ProductRowProps) => {
    const {
        user: { role },
    } = useContext(AuthenticationContext);
    const quantity = item.missing_quantity !== undefined ? item.missing_quantity : 0;
    const hasProductOrder = extraRowProps.hasProductOrder;
    const originalQuantity =
        hasProductOrder && extraRowProps.originalQuantities?.[item.id] !== undefined
            ? extraRowProps.originalQuantities[item.id]
            : 0;

    const isQuantityMatched = !hasProductOrder || quantity >= originalQuantity;
    const backgroundColor = isQuantityMatched ? "" : color.status.DANGER_LIGHT;
    const selectedQuantityBorderColor = isQuantityMatched ? "" : color.status.FAIL;

    const canSeePrice = ![ROLE.FIRST_STORAGE_KEEPER, ROLE.SECOND_STORAGE_KEEPER].includes(role);

    const provider_price = item.provider_price !== undefined ? formatCurrency(item.provider_price) : 0;

    return !item.isSummary ? (
        <TableRow sx={{ backgroundColor }}>
            <StyledTableCell align="left">
                <StyledNavLink to={`/materials/${item.id}`}>
                    {item.material_code ? item.material_code : item.id}
                </StyledNavLink>
            </StyledTableCell>
            <StyledTableCell align="left">{item.name}</StyledTableCell>
            <StyledTableCell align="left">{item.measurement_unit}</StyledTableCell>
            {canSeePrice && (
                <StyledTableCell align="left">{provider_price}</StyledTableCell>
            )}
            {hasProductOrder && <StyledTableCell align="left">{formatNumber(originalQuantity)}</StyledTableCell>}
            <StyledTableCell align="left">
                <TextBox
                    numeric
                    style={{
                        borderColor: selectedQuantityBorderColor,
                        backgroundColor,
                    }}
                    text={formatInternationalNumber(quantity ?? "")}
                    onChangeText={(value: string) => {
                        const n = parseInternationalToNumber(value, error);
                        if (!Number.isFinite(n)) return;
                        extraRowProps.onChangeMaterialMissingQuantity(item.id, n);
                    }}
                    disabled={extraRowProps.isView}
                />
            </StyledTableCell>
            <StyledTableCell align="left">
                {!extraRowProps.isView && (
                    <IconButton onClick={() => extraRowProps.removeItem(item)}>
                        <DeleteIcon />
                    </IconButton>
                )}
            </StyledTableCell>
        </TableRow>
    ) : (
        <SummaryTableRow>
            <SummaryTableCell align="left">{item.id}</SummaryTableCell>
            <SummaryTableCell align="left">{item.name}</SummaryTableCell>
            <SummaryTableCell align="left"></SummaryTableCell>
            {canSeePrice && (
                <SummaryTableCell align="left">{formatCurrency(item.price)}</SummaryTableCell>
            )}
            {hasProductOrder && <SummaryTableCell align="left"></SummaryTableCell>}
            <SummaryTableCell align="left"></SummaryTableCell>
            <SummaryTableCell align="left"></SummaryTableCell>
        </SummaryTableRow>
    );
};

interface MaterialListProps {
    materials: Material[];
    mergeMaterialOrder: Function;
    isView?: boolean;
    hasProductOrder?: boolean;
    providerId?: string | number | null;
    providerName?: string;
    sourceMaterials?: Material[];
}

export const MaterialList = ({
    materials,
    mergeMaterialOrder,
    isView,
    hasProductOrder = false,
    providerId,
    providerName,
    sourceMaterials,
}: MaterialListProps) => {
    const {
        user: { role },
    } = useContext(AuthenticationContext);
    const canSeePrice = ![ROLE.FIRST_STORAGE_KEEPER, ROLE.SECOND_STORAGE_KEEPER].includes(role);


    const [isMaterialModalOpen, setIsMaterialModalOpen] = useState(false);
    const [originalQuantities, setOriginalQuantities] = useState<{
        [key: string]: number;
    }>({});

    // Initialize original quantities when materials change
    React.useEffect(() => {
        if (hasProductOrder) {
            const newQuantities = { ...originalQuantities };
            materials.forEach((material) => {
                if (!newQuantities[material.id]) {
                    newQuantities[material.id] = material.missing_quantity || 0;
                }
            });
            setOriginalQuantities(newQuantities);
        }
    }, [materials.length, hasProductOrder]);

    let hasInvalidQuantity = false;
    if (hasProductOrder) {
        materials.forEach((material) => {
            if ((material.missing_quantity || 0) < (originalQuantities[material.id] || 0)) {
                hasInvalidQuantity = true;
            }
        });
    }

    const summaryMaterials = {
        id: `Tổng ${materials.length} vật tư`,
        name: "Subtotal",
        price: sum(materials.map((item: any) => (item.provider_price || 0) * (item.missing_quantity || 0))),
        isSummary: true,
    };

    const tableItems = materials.length === 0 ? [] : [...materials, summaryMaterials];

    const onRemoveMaterial = (material: Material) => {
        mergeMaterialOrder({
            materials: materials.filter((item) => item.id !== material.id),
        });
    };

    const onChangeMaterialMissingQuantity = (id: string, quantity: number) => {
        mergeMaterialOrder({
            materials: materials.map((material) =>
                material.id === id ? { ...material, missing_quantity: quantity } : material,
            ),
        });
    };

    const getColumns = () => {
        const baseColumns = [
            ...MaterialTableColumns.slice(0, 4),
            ...(hasProductOrder ? [{ text: "Số lượng thiếu", icon: <></> }] : []),
            ...MaterialTableColumns.slice(4),
        ];

        if (!canSeePrice) {
            return baseColumns.filter((column) => column.text !== "Giá cung cấp");
        }

        return baseColumns;
    };

    return (
        <>
            {isMaterialModalOpen && !isView && (
                <AddMaterialModal
                    selectedMaterials={materials}
                    providerId={providerId}
                    providerName={providerName}
                    sourceMaterials={sourceMaterials}
                    open={isMaterialModalOpen}
                    handleClose={() => setIsMaterialModalOpen(false)}
                    mergeMaterialOrder={mergeMaterialOrder}
                />
            )}

            <ContentWrapper>
                <ProductTableText>Danh sách vật tư</ProductTableText>

                {hasProductOrder && hasInvalidQuantity && (
                    <TableWarningMessage>
                        <WarningTableIcon />
                        <span
                            style={{
                                display: "flex",
                                alignItems: "center",
                                whiteSpace: "pre-wrap",
                            }}
                        >
                            <span style={{ fontWeight: "700" }}>Lưu ý quan trọng: </span>
                            Số lượng yêu cầu không được nhỏ hơn số lượng thiếu.
                        </span>
                    </TableWarningMessage>
                )}

                <CustomTable
                    itemList={tableItems}
                    RowElement={MaterialRow}
                    TableColumns={getColumns()}
                    extraRowProps={{
                        removeItem: onRemoveMaterial,
                        onChangeMaterialMissingQuantity,
                        originalQuantities,
                        hasProductOrder,
                        isView,
                    }}
                />

                {materials.length === 0 && "Không có vật tư được chọn"}
                {!isView && !providerId && "Vui lòng chọn nhà cung cấp trước khi thêm vật tư"}
                {!isView && (
                    <ProductButtonRow>
                        <StyledButton
                            variant="contained"
                            disabled={!providerId}
                            onClick={() => setIsMaterialModalOpen(true)}
                        >
                            Thêm vật tư
                        </StyledButton>
                    </ProductButtonRow>
                )}
            </ContentWrapper>
        </>
    );
};
